import React, { useCallback, useEffect, useState } from "react";
import { api } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { Card } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Checkbox } from "../components/ui/checkbox";
import { toast } from "sonner";
import { apiErrorMessage } from "../lib/inventoryApi";
import Hint from "../components/etams/Hint";
// Kamus nama jenis dipakai BERSAMA kartu Tugas Saya — satu sumber, supaya
// layar setelan dan layar tugas nol pernah menyebut hal yang sama dengan dua
// nama berbeda.
import { namaJenisNotif, GRUP, namaGrup } from "../lib/notifikasi";

// K19 — saklar kanal per JENIS notifikasi, disetel Owner tanpa coding.
//
// K116 mengunci urutannya: saklar DULU, baru eskalasi. Alasannya di badan
// K116 — pengingat yang nol bisa dimatikan adalah pengingat yang akan
// diabaikan, lalu seluruh kanalnya ikut diabaikan bersamanya.
//
// ⚠ NOL tombol Tambah / Hapus, dan itu disengaja. Himpunan jenis notifikasi
// ditentukan CHECK constraint di database — ia tumbuh lewat migration, bukan
// lewat layar ini.

const KANAL = [
  { nilai: "APP", label: "Aplikasi", ket: "Kartu Tugas Saya di Dashboard" },
  { nilai: "BOTH", label: "Aplikasi + WA", ket: "Siap saat WhatsApp aktif" },
  {
    nilai: "WA",
    label: "WhatsApp saja",
    ket: "Belum tersedia",
    terkunci: true,
  },
];


export default function MasterNotificationChannels() {
  const { isOwner } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sibuk, setSibuk] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/master/notification-channels");
      setItems(r.data?.items || []);
    } catch (e) {
      toast.error(apiErrorMessage(e, "Gagal memuat setelan"));
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  async function ubah(type, patch) {
    setSibuk(type);
    try {
      await api.patch(`/master/notification-channels/${type}`, patch);
      await load();
    } catch (e) {
      // Pesan backend ditampilkan APA ADANYA — pagar 'WA' menjelaskan
      // sebabnya di situ, dan meringkasnya jadi "gagal" membuang justru
      // bagian yang berguna.
      toast.error(apiErrorMessage(e, "Gagal menyimpan"));
    } finally {
      setSibuk(null);
    }
  }

  // ⚠ Judul diambil dari GRUP, BUKAN diketik ulang. Daftar yang diketik
  // tangan akan diam-diam kehilangan grup baru — barisnya hilang dari layar
  // tanpa satu pun galat.
  const grup = [...GRUP.map((g) => g.judul), "Lainnya"]
    .map((judul) => ({ judul, baris: items.filter((i) => namaGrup(i.type) === judul) }))
    .filter((g) => g.baris.length > 0);

  return (
    <div className="space-y-6" data-testid="master-notif-channels-page">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">
          Pengaturan Notifikasi{" "}
          <Hint>Tiap jenis notifikasi punya saklarnya sendiri. Mematikan sebuah jenis menghentikan notifikasi baru; yang sudah terbit tetap ada sampai tugasnya selesai.</Hint>
        </h1>
      </div>

      {!isOwner && (
        <Card className="p-4 border-info-soft-border bg-info-soft" data-testid="notif-channel-readonly">
          <div className="text-sm text-info-soft-foreground">
            Anda bisa <strong>melihat</strong> setelan ini, tapi hanya Owner yang
            bisa mengubahnya.
          </div>
        </Card>
      )}

      {loading ? (
        <Card className="p-6 text-center text-sm text-muted-foreground">Memuat...</Card>
      ) : items.length === 0 ? (
        <Card className="p-6 text-center text-sm text-muted-foreground">
          Belum ada setelan.
        </Card>
      ) : (
        grup.map((g) => (
          <Card className="p-6" key={g.judul} data-testid={`notif-grup-${g.judul}`}>
            <h2 className="text-base font-semibold mb-4">{g.judul}</h2>
            <div className="space-y-3">
              {g.baris.map((it) => (
                <div
                  key={it.type}
                  className="flex items-start justify-between gap-4 flex-wrap p-3 rounded border border-border"
                  data-testid={`notif-channel-${it.type}`}
                >
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-medium">{namaJenisNotif(it.type)}</span>
                      {/* Kode aslinya TETAP ditampilkan, kecil, di sampingnya.
                          Ia yang muncul di galat, audit, dan pertanyaan ke
                          agent — menghilangkannya sama sekali membuat layar
                          ini nol bisa dipakai saat menelusuri masalah. */}
                      {isOwner && <code className="text-[10px] text-muted-foreground">{it.type}</code>}
                      {!it.aktif && (
                        <Badge className="bg-danger-soft text-danger-soft-foreground border-danger-soft-border border text-[10px]">
                          Dimatikan
                        </Badge>
                      )}
                    </div>
                    {isOwner && it.catatan && (
                      <span className="ml-1 align-middle"><Hint label="Catatan kanal">{it.catatan}</Hint></span>
                    )}
                  </div>

                  <div className="flex items-center gap-4 flex-wrap">
                    <div className="flex items-center gap-1.5">
                      {KANAL.map((k) => (
                        <Button
                          key={k.nilai}
                          size="sm"
                          variant={it.kanal === k.nilai ? "default" : "outline"}
                          disabled={!isOwner || sibuk === it.type || k.terkunci}
                          title={k.ket}
                          onClick={() => ubah(it.type, { kanal: k.nilai })}
                          data-testid={`notif-kanal-${it.type}-${k.nilai}`}
                        >
                          {k.label}
                        </Button>
                      ))}
                    </div>
                    <label className="flex items-center gap-2 text-sm">
                      <Checkbox
                        checked={!!it.aktif}
                        disabled={!isOwner || sibuk === it.type}
                        onCheckedChange={(v) => ubah(it.type, { aktif: !!v })}
                        data-testid={`notif-aktif-${it.type}`}
                      />
                      Aktif
                    </label>
                  </div>
                </div>
              ))}
            </div>
          </Card>
        ))
      )}

      <Card className="p-4" data-testid="notif-channel-catatan">
        <div className="text-xs text-muted-foreground leading-relaxed">
          <strong>WhatsApp saja</strong> sengaja dikunci: selama modul WhatsApp
          belum ada, setelan itu membuat notifikasinya hilang sama sekali — tanpa
          kartu tugas, tanpa pesan. Pakai{" "}
          <strong>Aplikasi + WA</strong> kalau ingin siap saat WhatsApp aktif.
        </div>
      </Card>
    </div>
  );
}
